import { DataTable } from "@/components/data-table/data-table";
import { FC, useEffect, useMemo } from "react";
import { getConversationColumns } from "./Columns";

import useConversationTableData from "../hooks/useConversationTableData";
import usePiiOverride from "@/hooks/usePiiOverride";
import usePiiFeatureEnabled from "@/hooks/usePiiFeatureEnabled";
import { PiiOverrideConversationSummary } from "@/models/pii/PiiOverride";
import { logger } from "@/lib/logger";
import { useHasPermission } from "@/lib/permissions/permissionFunctions";
import { Permission } from "@/lib/permissions/Permission.enum";
import type { DateRange } from "@capacit-com/lara-law-shared-ui";
import { DateTime } from "luxon";
import { ConversationTableRowSkeleton } from "./ConversationTableRowSkeleton";

type ConversationTableProps = {
  dateRange: DateRange | undefined;
};

const ConversationTable: FC<ConversationTableProps> = ({ dateRange }) => {
  const {
    data,
    pageCount,
    pageIndex,
    pageSize,
    sorting,
    searchValue,
    columnFilters,
    setSearchValue,
    setPageIndex,
    setPageSize,
    setSorting,
    handleRowClick,
    setColumnFilters,
    isLoading,
    users,
  } = useConversationTableData();

  // PII-override badge data (LAR-27 follow-up): ONE batch POST for the visible
  // page instead of a GET per row. Inert while the PII-Redaction flag is off.
  const piiFeatureEnabled = usePiiFeatureEnabled();
  const conversationIds = useMemo(
    () => data.map((conversation) => conversation.id),
    [data]
  );
  const { useQueryConversationSummaries } = usePiiOverride();
  const summariesQuery = useQueryConversationSummaries(
    conversationIds,
    piiFeatureEnabled
  );

  useEffect(() => {
    if (!summariesQuery.isError) return;
    // Badge is a progressive enhancement: on failure the list still renders,
    // so log instead of toasting.
    logger.warn("Failed to load PII override summaries for Insights list", {
      operation: "getPiiOverrideConversationSummaries",
      conversationCount: conversationIds.length,
      error: summariesQuery.error,
    });
  }, [summariesQuery.isError, summariesQuery.error, conversationIds.length]);

  const piiOverrideSummaries = useMemo(() => {
    if (!piiFeatureEnabled) return undefined;
    return new Map<string, PiiOverrideConversationSummary>(
      (summariesQuery.data ?? []).map((summary) => [
        summary.conversationId,
        summary,
      ])
    );
  }, [piiFeatureEnabled, summariesQuery.data]);

  useEffect(() => {
    if (dateRange?.from && dateRange?.to) {
      const fromDate = DateTime.fromJSDate(dateRange.from).toFormat(
        "yyyy-MM-dd'T'HH:mm:ss"
      );
      const toDate = DateTime.fromJSDate(dateRange.to).toFormat(
        "yyyy-MM-dd'T'HH:mm:ss"
      );
      setColumnFilters([
        {
          id: "createdAt_gte",
          value: fromDate,
        },
        {
          id: "createdAt_lte",
          value: toDate,
        },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  // Admin delete per row (LAR-367 #4) — the column is only built for a viewer
  // who can actually act on it.
  const hasPermission = useHasPermission();
  const canDeleteConversations = hasPermission({
    allOf: [Permission.DeleteAnyConversation],
  });

  return (
    <DataTable
      columns={useMemo(
        () =>
          getConversationColumns(
            users,
            piiOverrideSummaries,
            canDeleteConversations
          ),
        [users, piiOverrideSummaries, canDeleteConversations]
      )}
      data={data}
      pageCount={pageCount}
      pageIndex={pageIndex}
      pageSize={pageSize}
      setPageIndex={setPageIndex}
      setPageSize={setPageSize}
      sorting={sorting}
      setSorting={setSorting}
      handleSearchChange={setSearchValue}
      search={searchValue}
      handleRowClick={handleRowClick}
      columnFilters={columnFilters}
      setColumnFilters={setColumnFilters}
      isLoading={isLoading}
      hasToolbar={false}
      stickyHeader={true}
      pageSizeOptions={[10, 25, 50, 75, 100]}
      skeletonComponent={ConversationTableRowSkeleton}
      skeletonRows={pageSize}
    />
  );
};

export default ConversationTable;
